import { assertUsablePdf } from '@/features/compliance/demo/showModeArtifacts'
import {
  emptyShowRecord,
  showRecordSchema,
  type ShowRecord,
  type ShowScope,
} from '@/features/compliance/demo/showModeModel'

export const SHOW_STORAGE_PREFIX = 'ail.compliance.show.v1:'
type StoragePort = Pick<Storage, 'getItem' | 'setItem'>
export function showStorageKey(scope: ShowScope) {
  return `${SHOW_STORAGE_PREFIX}${encodeURIComponent(scope.locationId)}:${encodeURIComponent(scope.customerId)}`
}

/** Validate before disclosure; the final compare/write stays synchronous within this tab. */
export function createShowRepository(storage: StoragePort) {
  function rawValue(scope: ShowScope) {
    try {
      return storage.getItem(showStorageKey(scope))
    } catch {
      throw new Error(
        'Der Browserspeicher ist nicht verfügbar. Bitte prüfen Sie die Browsereinstellungen.',
      )
    }
  }
  async function validate(record: ShowRecord) {
    for (const document of record.documents) await assertUsablePdf(document.pdf)
  }
  async function snapshot(scope: ShowScope) {
    const raw = rawValue(scope)
    if (raw === null) return { raw, record: emptyShowRecord(scope) }
    try {
      const record = showRecordSchema.parse(JSON.parse(raw))
      if (
        record.scope.customerId !== scope.customerId ||
        record.scope.locationId !== scope.locationId
      )
        throw new Error('scope')
      await validate(record)
      return { raw, record }
    } catch {
      throw new Error(
        'Die gespeicherten Demodaten konnten nicht gelesen werden. Sie wurden nicht überschrieben.',
      )
    }
  }
  return {
    async read(scope: ShowScope) {
      const current = await snapshot(scope)
      if (rawValue(scope) !== current.raw)
        throw new Error('Die Daten wurden inzwischen geändert. Bitte laden Sie die Ansicht neu.')
      return current.record
    },
    async write(
      scope: ShowScope,
      expectedRevision: number,
      update: (current: ShowRecord) => ShowRecord,
      beforeCommit?: () => void,
    ) {
      const { raw, record: current } = await snapshot(scope)
      if (current.revision !== expectedRevision)
        throw new Error('Die Daten wurden inzwischen geändert. Bitte laden Sie die Ansicht neu.')
      const next = showRecordSchema.parse(update(current))
      if (
        next.revision !== expectedRevision + 1 ||
        next.scope.customerId !== scope.customerId ||
        next.scope.locationId !== scope.locationId
      )
        throw new Error('Ungültiger Speicherstand.')
      await validate(next)
      beforeCommit?.()
      if (rawValue(scope) !== raw)
        throw new Error('Die Daten wurden inzwischen geändert. Bitte laden Sie die Ansicht neu.')
      try {
        storage.setItem(showStorageKey(scope), JSON.stringify(next))
      } catch {
        throw new Error(
          'Speichern fehlgeschlagen: Der Browserspeicher ist voll oder gesperrt. Ihre Eingabe bleibt für einen erneuten Versuch erhalten.',
        )
      }
      return next
    },
  }
}
